// Formatting dates in the SITE'S timezone, not the reader's.
//
// WHY NOT toLocaleString() ON ITS OWN
//
// A competition is announced at a time, and that time belongs to the
// site. "Starts at 8pm" has to mean the same thing to a buyer in Madrid
// and an admin in Manchester, or the announcement is meaningless.
//
// The browser's own timezone looks like the natural choice and is exactly
// wrong for this: it makes the announced time follow the reader around.
// Two people comparing notes on the same competition would disagree about
// when it starts.
//
// On 30 August 2026 the server and the browser disagreed by an hour — the
// server rendered UTC, the browser rendered BST — and a correct schedule
// suggestion looked like a time already past. We went looking for a bug
// in the scheduler twice before finding the display.
//
// THE ZONE COMES FROM THE SERVER, once, and every formatter here uses it.
// Until it arrives we fall back to Europe/London rather than to the
// browser: a wrong-but-consistent zone is easier to spot and less harmful
// than one that varies per reader.

let siteTimeZone = "Europe/London";

// Called when the config response arrives. Safe to call repeatedly; an
// unknown or missing value leaves the previous one in place rather than
// breaking every date on the page.
export function setSiteTimeZone(tz) {
  if (typeof tz !== "string" || !tz) return;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz }).format(0);
    siteTimeZone = tz;
  } catch {
    // Not a zone this browser knows. Keep what we had.
  }
}

export function getSiteTimeZone() {
  return siteTimeZone;
}

function withZone(options) {
  return { timeZone: siteTimeZone, ...options };
}

// Date and time — the everyday one, matching the server's own messages.
export function siteDateTime(value) {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", withZone({ dateStyle: "medium", timeStyle: "short" }));
}

// Date only.
export function siteDate(value) {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", withZone({ dateStyle: "medium" }));
}

// Time only.
export function siteTime(value) {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", withZone({ timeStyle: "short" }));
}

// With seconds, for logs and audit trails where the exact moment matters.
export function siteDateTimeSeconds(value) {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", withZone({ dateStyle: "medium", timeStyle: "medium" }));
}

// The zone itself, for labelling a screen once rather than every row —
// "all times Europe/London" beats repeating it forty-seven times.
export function siteTimeZoneLabel() {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: siteTimeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === "timeZoneName");
    return name ? `${siteTimeZone} (${name.value})` : siteTimeZone;
  } catch {
    return siteTimeZone;
  }
}
